<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { ArrowDown } from "@element-plus/icons-vue";
import { useUserStore } from "@/stores/user";
import { useMessageStore } from "@/stores/message";
import PersonPanel from "./PersonPanel.vue";

import logoImg from "@/assets/img/common/Logo_2.png";
import logoDarkImg from "@/assets/img/common/Logo_dark.png";
import aiMindIcon from "@/assets/img/common/headerbar/AI_1.png";
import aiChatIcon from "@/assets/img/common/headerbar/AI_2.png";
import taskIcon from "@/assets/img/common/headerbar/panel-icon8.png";
import networkIcon from "@/assets/img/common/headerbar/topbar-icon2.png";
import { useTheme } from "@/composables/useTheme";

const router = useRouter();
const userStore = useUserStore();
const messageStore = useMessageStore();
const { isSoftMode } = useTheme();

const panelVisible = ref(false);
const currentLogo = computed(() => (isSoftMode.value ? logoDarkImg : logoImg));

const displayName = computed(() => {
  const name = userStore.fullName || "Tutor";
  const tuid = userStore.tuid || "";
  return tuid ? `${name}[${tuid}]` : name;
});

const avatarUrl = computed(() => userStore.avatar || userStore.defaultAvatar);
const msgTotal = computed(() => Number(messageStore.homeMsg) || 0);
const badgeText = computed(() => (msgTotal.value > 99 ? "99+" : String(msgTotal.value)));

function onAvatarError(e) {
  e.target.src = userStore.defaultAvatar;
}

function goMessage() {
  router.push({ path: "/message" });
}

function goNetworkTest() {
  window.open("/speed/speed.html");
}

function goAiMind() {
  router.push({ path: "/aiChat", query: { systemType: 5 } });
}

function goAiChat() {
  router.push({ path: "/aiChat" });
}

function goTeacherTask() {
  router.push({ path: "/teacherTask" });
}

function closePanel() {
  panelVisible.value = false;
}

onMounted(() => {
  messageStore.fetchUnreadTotals();
});
</script>

<template>
  <header class="app-header my-headers">
    <router-link to="/home" class="app-header__logo" title="home">
      <img :src="currentLogo" alt="ACADSOC" class="home-icon" width="142" />
    </router-link>

    <div class="app-header__tools">
      <button type="button" class="tool-btn" title="AI Mind" @click="goAiMind">
        <img :src="aiMindIcon" alt="AI Mind" />
      </button>
      <button type="button" class="tool-btn" title="AI Chat" @click="goAiChat">
        <img :src="aiChatIcon" alt="AI Chat" />
      </button>
      <button type="button" class="tool-btn" title="Teacher Task" @click="goTeacherTask">
        <img :src="taskIcon" alt="Teacher Task" />
      </button>
      <button
        type="button"
        class="tool-btn"
        title="Network Test"
        @click="goNetworkTest"
      >
        <img :src="networkIcon" alt="Network Test" />
      </button>

      <button type="button" class="tool-btn header-notice" title="Message" @click="goMessage">
        <el-badge :value="badgeText" :hidden="msgTotal <= 0" type="danger" :max="99">
          <span class="header-notice-icon" />
        </el-badge>
      </button>

      <el-popover
        v-model:visible="panelVisible"
        placement="bottom-end"
        :width="280"
        trigger="hover"
        :show-arrow="false"
        :offset="8"
        popper-class="person-panel-popper"
      >
        <template #reference>
          <button type="button" class="user-trigger">
            <img :src="avatarUrl" alt="" width="36" height="36" @error="onAvatarError" />
            <span class="blue-title">{{ displayName }}</span>
            <el-icon class="header-panel-icon"><ArrowDown /></el-icon>
          </button>
        </template>
        <PersonPanel @close="closePanel" />
      </el-popover>
    </div>
  </header>
</template>

<style scoped lang="scss">
.my-headers {
  height: $layout-header-height;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px 0 24px;
  background: var(--app-header-bg, #fff);
  box-shadow: var(--app-shadow, 0 2px 10px rgba(125, 162, 255, 0.19));
  color: var(--app-text, $blue-title);
}

.app-header__logo {
  display: flex;
  align-items: center;
  flex-shrink: 0;

  .home-icon {
    display: block;
    width: 142px;
    height: auto;
  }
}

@media (max-width: $mobile-breakpoint) {
  .app-header__logo {
    display: none;
  }
}

.app-header__tools {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0 6px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: var(--app-hover, rgba(62, 103, 251, 0.06));
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
}

.header-notice {
  margin-right: 12px;

  .header-notice-icon {
    display: block;
    width: 20px;
    height: 20px;
    background: url("@/assets/img/common/headerbar/topbar-icon3.png") no-repeat center;
    background-size: contain;
  }
}

.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 4px;
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--app-hover, rgba(62, 103, 251, 0.04));

    .header-panel-icon {
      color: $blue-title-2;
    }
  }

  img {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
  }

  .blue-title {
    font-size: 15px;
    font-weight: 400;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--app-text, $blue-title);
  }

  .header-panel-icon {
    color: #b3d4fc;
    font-size: 20px;
    font-weight: bold;
  }
}
</style>

<style lang="scss">
.person-panel-popper.el-popover {
  padding: 0 !important;
  border: none !important;
  border-radius: 8px !important;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(153, 171, 234, 0.25) !important;
  filter: drop-shadow(0 4px 16px rgba(153, 171, 234, 0.25));
}
</style>
